import React, { useEffect } from 'react';
import { Layout } from 'antd';
import { useStyleContext } from '../../context/StyleContext';
import { useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';

interface EmbedContainerProps {
  EmbedComponent: (...props: any) => JSX.Element;
}
const { Content } = Layout;

/**
 * EmbedContainer Component
 *
 * A reusable container for embedding ThoughtSpot components like Liveboard and App.
 * It manages state related to styling, real-time updates, and provides an `embedRef`
 * to the embedded component for interaction handling.
 */
export const EmbedContainer: React.FC<EmbedContainerProps> = ({ EmbedComponent, ...restProps }) => {
  const { styleState, styleStateKey } = useStyleContext();
  const [variables, setVariables] = React.useState(styleState);
  const embedRef = useEmbedRef();
  const { getCustomFontRules } = useStyleContext();
  // Get all custom font rules and memoize them
  const customFontRules = React.useMemo(() => getCustomFontRules(), [getCustomFontRules]);

  useEffect(() => {
    if (embedRef?.current && 'on' in embedRef.current) {
      embedRef.current.on('DynamicCssNodeReady' as any, () => {
        setVariables(styleState);
      });
    }
  }, [embedRef, styleStateKey]);

  useEffect(() => {
    if (embedRef?.current && 'trigger' in embedRef.current) {
      embedRef.current.trigger('UpdateCustomCss' as any, {
        customizations: {
          style: {
            customCSS: {
              variables: styleState,
              rules_UNSTABLE: {
                ...customFontRules,
              },
            },
          },
        },
      });
      localStorage.setItem(styleStateKey, JSON.stringify(styleState));
    } else setVariables(styleState);
  }, [styleState]);

  return (
    <div
      style={{ height: '100%', width: '100%', border: '1px solid var(--rd-sys-color-border-bold)' }}
      role="main"
      aria-label="ThoughtSpot embed container"
    >
      <Content style={{ height: '100%', width: '100%' }}>
        <EmbedComponent embedRef={embedRef} variables={variables} {...restProps} />
      </Content>
    </div>
  );
};
